/**
 * Consultant page domain model.
 *
 * Types and pure mapping helpers for the Consultant page. All data comes from
 * the backend (tng.SideMenuContent stored procedure via REST) — there is no
 * seed/mock data here. See @src/rest/CentralCalls for the raw response shapes.
 */

import type { SemanticICONS }                      from 'semantic-ui-react';
import type { SideMenuClient, SideMenuConsultant } from '@src/rest/CentralCalls';

// ─── Segments ─────────────────────────────────────────────────────────────────

export type DashSegment = 'executive' | 'consultant' | 'client';

export const DASH_SEGMENTS: DashSegment[] = [ 'executive', 'consultant', 'client' ];

export const isDashSegment = ( value: string ): value is DashSegment =>
    DASH_SEGMENTS.some( segment => segment === value );

// ─── Entities (backed by dbo.Client / dbo.ClientEmployee) ─────────────────────

export interface Company {
    id: string;
    name: string;
}

export interface Consultant {
    id: string;
    name: string;
    title: string;
    /** dbo.ClientEmployee.AccountId */
    accountId?: string;
    /** dbo.ClientEmployee.AccessLevel */
    accessLevel?: string;
}

/** Maps a dbo.Client row from the SideMenuContent proc onto the page Company model. */
export const companyFromDbClient = ( client: SideMenuClient ): Company => ( {
    id:   client.id,
    name: client.name,
} );

/** Maps a dbo.ClientEmployee row from the SideMenuContent proc onto the page Consultant model. */
export const consultantFromDbRow = ( row: SideMenuConsultant ): Consultant => ( {
    id:          row.id,
    name:        row.fullName,
    title:       row.accessLevel ?? 'Consultant',
    accountId:   row.accountId,
    accessLevel: row.accessLevel ?? undefined,
} );

// ─── Sidebar model ────────────────────────────────────────────────────────────

export type SidebarEntityKind = 'company' | 'consultant' | 'executive' | 'reports' | 'finance';

export interface SidebarEntity {
    id: string;
    kind: SidebarEntityKind;
    name: string;
}

export const REPORTS_ENTITY_ID = 'consultant-dash-reports';

export const REPORTS_ENTITY: SidebarEntity = {
    id:   REPORTS_ENTITY_ID,
    kind: 'reports',
    name: 'Reports',
};

export const FINANCE_ENTITY_ID = 'consultant-dash-finance';

export const FINANCE_ENTITY: SidebarEntity = {
    id:   FINANCE_ENTITY_ID,
    kind: 'finance',
    name: 'Finance',
};

export interface SidebarSection {
    kind: SidebarEntityKind;
    label: string;
    entities: SidebarEntity[];
}

/**
 * Builds the sidebar sections for the current segment from backend-loaded
 * companies and consultants.
 */
export const getSidebarSections = (
    segment: DashSegment,
    identityId: string | undefined,
    companies: Company[],
    consultants: Consultant[]
): SidebarSection[] => {

    if( segment === 'consultant' ){
        return [
            {
                kind:     'company',
                label:    'Companies',
                entities: companies.map( c => ( { id: c.id, kind: 'company', name: c.name } ) ),
            },
        ];
    }

    if( segment === 'client' ){
        const company = identityId
            ? companies.find( c => c.id === identityId )
            : companies[ 0 ];
        if( !company ) return [];

        return [
            {
                kind:     'company',
                label:    'Your Engagement',
                entities: [ { id: company.id, kind: 'company', name: company.name } ],
            },
        ];
    }

    // executive — full overview (admin sees every client and consultant)
    return [
        {
            kind:     'reports',
            label:    '',
            entities: [ REPORTS_ENTITY ],
        },
        {
            kind:     'company',
            label:    'Companies',
            entities: companies.map( c => ( { id: c.id, kind: 'company', name: c.name } ) ),
        },
        {
            kind:     'consultant',
            label:    'Consultants',
            entities: consultants.map( c => ( { id: c.id, kind: 'consultant', name: c.name } ) ),
        },
    ];
};

/** The views a consultant can switch between for the client selected in the sidebar. */
export type ConsultantDashView = 'workspace' | 'dashboard';

export interface ConsultantDashViewOption {
    value: ConsultantDashView;
    label: string;
    icon: SemanticICONS;
}

export const CONSULTANT_DASH_VIEWS: ConsultantDashViewOption[] = [
    { value: 'workspace', label: 'Workspace', icon: 'folder open outline' },
    { value: 'dashboard', label: 'Dashboard', icon: 'chart bar outline' },
];

export const isConsultantDashView = ( value: string ): value is ConsultantDashView =>
    CONSULTANT_DASH_VIEWS.some( view => view.value === value );
